'use client';

/**
 * PODetails - Purchase Order entity detail view
 *
 * @deprecated Use EntityDetailsLoader with DetailsPanel instead.
 * This component is kept for fallback during migration.
 * Remove after schema-driven rendering is verified.
 *
 * Displays PO information when a PO node is selected.
 * Fetches project data to find the specific PO, plus vendor contacts.
 *
 * @component_role PO Detail View
 * @business_rule Shows PO status, vendor info with contacts, line items
 *   with totals. Links to Odoo purchase order form.
 * @user_outcome Quick PO inspection - see what was ordered, from whom,
 *   total cost, and vendor contact for follow-up.
 * @integration_point klyde_edge /neo/graph/explore/project, /neo/graph/vendor/contacts
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { Factory } from 'lucide-react';
import { useProjectExplore, useVendorContacts } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

// Shared components
import {
  StatusBadge,
  SectionHeader,
  VendorContactsList,
  LoadingState,
  LineItemsTable,
  OdooLink,
} from './shared';

// ============================================================
// Main Component
// ============================================================

interface PODetailsProps {
  nodeId: string;
  projectId?: string | null;
}

export function PODetails({ nodeId, projectId }: PODetailsProps) {
  const projectExploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const vendorContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_contacts'));

  // Fetch project data to find the specific PO
  const { data: projectData, isLoading: projectLoading } = useProjectExplore(
    projectExploreStrategy!,
    projectId
      ? {
          project_id: projectId,
          include_relationships: ['purchase_orders'],
          max_items: 50,
        }
      : null
  );

  // Find the specific PO from project data
  const po = projectData?.project?.purchase_orders.find((p) => p.semantic_id === nodeId);

  // Fetch vendor contacts if PO has a vendor
  const { data: vendorContactsData, isLoading: contactsLoading } = useVendorContacts(
    vendorContactsStrategy!,
    po?.vendor?.semantic_id ? { vendor_id: po.vendor.semantic_id } : null
  );
  const vendorContacts = vendorContactsData?.contacts || [];

  // Loading state
  if (projectLoading) {
    return <LoadingState message="Loading PO..." />;
  }

  // No project context
  if (!projectId) {
    return (
      <div className="space-y-4">
        <div>
          <SectionHeader title="SEMANTIC ID" />
          <code className="text-xs bg-muted px-2 py-1 rounded block overflow-x-auto">
            {nodeId}
          </code>
        </div>
        <div className="text-sm text-muted-foreground italic">
          Select from a project graph to see full details
        </div>
      </div>
    );
  }

  // PO not found
  if (!po) {
    return (
      <div className="text-sm text-muted-foreground">
        PO details not available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status */}
      <div>
        <SectionHeader title="STATUS" />
        <StatusBadge status={po.state} type="po" />
      </div>

      {/* Semantic ID */}
      <div>
        <SectionHeader title="SEMANTIC ID" />
        <code className="text-xs bg-muted px-2 py-1 rounded block overflow-x-auto">
          {nodeId}
        </code>
      </div>

      {/* Vendor */}
      {po.vendor && (
        <div>
          <SectionHeader title="VENDOR" />
          <div className="flex items-center gap-2">
            <Factory className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{po.vendor.name}</span>
          </div>
          <VendorContactsList
            contacts={vendorContacts}
            isLoading={contactsLoading}
            maxDisplay={3}
          />
        </div>
      )}

      {/* Line Items */}
      <div>
        <SectionHeader title="LINE ITEMS" count={po.line_items.length} />
        <LineItemsTable
          items={po.line_items}
          showTotal={true}
          priceColumn="total"
          priceLabel="Total"
          total={po.amount_total}
        />
      </div>

      {/* Open in Odoo */}
      {po.odoo_id && (
        <div>
          <SectionHeader title="ACTIONS" />
          <OdooLink
            odooId={po.odoo_id}
            model="purchase.order"
            label={po.name || `PO ${po.odoo_id}`}
          />
        </div>
      )}
    </div>
  );
}
